import { Typography } from "@mui/material";
import { ReactElement } from "react";

/**
 * Retrieves the typography style based on the variant and wrap properties.
 * @param {string} variant - The variant of the typography.
 * @param {string} wrap - The wrap property for text wrapping.
 * @param {string} cl - The color property for the typography.
 * @returns {object} - The typography style object.
 */
const getTypographyStyle = (
  variant?: string,
  wrap?: string,
  cl?: string
): object => {
  const baseStyles = {
    fontFamily: "'Poppins'",
    fontStyle: "normal",
    fontWeight: "400",
    fontFeatureSettings: "'pnum' on, 'lnum' on",
    whiteSpace: "nowrap",
    overflowWrap: "anywhere",
    color: "#3A3A3A",
    cursor: 'text'
  };

  const typographyStyle = {
    ...baseStyles,
    color: cl || baseStyles.color,
  };

  switch (variant) {
    case "one":
      return {
        ...typographyStyle,
        fontWeight: "700",
        fontSize: "1.625rem",
        lineHeight: "36px",
      };
    case "two":
      return {
        ...typographyStyle,
        fontWeight: "700",
        fontSize: "1.25rem",
        lineHeight: "36px",
      };
    case "three":
      return {
        ...typographyStyle,
        fontSize: "1rem",
        lineHeight: "24px",
        textWrap: wrap || "nowrap",
      };
    case "four":
      return {
        ...typographyStyle,
        fontSize: "0.813rem",
        lineHeight: "20px",
      };
      case "fifth":
        return {
          ...typographyStyle,
          fontSize: 12,
          lineHeight: "16px",
        };
        case "sixth":
          return {
            ...typographyStyle,
            fontSize: 10,
            lineHeight: "16px",
          };
    default:
      return typographyStyle;
  }
};

/**
 * TypographyLevel component for rendering typography with custom styles.
 * @param {TypographyLevelProps} props - The component props.
 * @returns {ReactElement} - The rendered component.
 */
const TypographyLevel = ({
  children,
  sx,
  variant,
  wrap,
  color,
  ...dynamicProps
}: any): ReactElement => {
  const typographyStyle = getTypographyStyle(variant, wrap, color);

  const mergedStyles = sx ? { ...typographyStyle, ...sx } : typographyStyle;

  return (
    <Typography sx={mergedStyles} {...dynamicProps}>
      {children}
    </Typography>
  );
};

export default TypographyLevel;
